import React, { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { fetchCsrfToken } from './api';

// Error Boundary for catching React errors
import ErrorBoundary from "./components/ErrorBoundary";
import { BrandingProvider } from './context/BrandingContext';

// Layout
import AppLayout from "./layouts/AppLayout.jsx";

// Core pages
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Dashboard from "./pages/Dashboard";
import FounderDashboard from "./pages/FounderDashboard";
import Clients from "./pages/Clients";
import Tasks from "./pages/Tasks";
import Policies from "./pages/Policies";
import Settings from "./pages/Settings";
import PhoneBlastPage from "./pages/PhoneBlastPage";
import Automations from "./pages/Automations";
import AEPWizard from "./pages/AEPWizard";
import AEPReadiness from "./pages/AEPReadiness";
import OEPHub from "./pages/OEPHub";
import Calendar from "./pages/Calendar";
import RiskManagement from "./pages/RiskManagement";
import ServiceDashboard from "./pages/ServiceDashboard";
import ServiceWorkspace from "./pages/ServiceWorkspace";
import Communications from "./pages/Communications";
import CommunicationsCenter from "./pages/CommunicationsCenter";
import Commissions from "./pages/Commissions";

// Auth flow
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";

// Import/Export helpers
import ImportClients from "./pages/ImportClients";
import ExportClients from "./pages/ExportClients";

// Client profile (you already have this file)
import ClientProfile from "./pages/ClientProfile";

// New: full add page
import AddClient from "./pages/AddClient";

// Founder Command Center pages
import FounderCommandCenter from "./pages/FounderCommandCenter";
import AgentAdoption from "./pages/founder/AgentAdoption";
import ClientQuality from "./pages/founder/ClientQuality";
import ValueDelivery from "./pages/founder/ValueDelivery";
import SystemHealth from "./pages/founder/SystemHealth";
import ReportsPage from "./pages/founder/ReportsPage";

// Audit Logs page
import AuditLogsPage from "./pages/AuditLogsPage";

// Marketing pages
import HomeSimple from "./pages/marketing/HomeSimple";
import WaitlistLanding from "./pages/marketing/WaitlistLanding";
import Pricing from "./pages/Pricing";

// SEO Landing Pages
import LearnIndex from "./pages/learn/LearnIndex";
import LearnPage from "./pages/learn/LearnPage";
import SolutionPage from "./pages/solutions/SolutionPage";

// Blog/Article Pages
import BlogPage from "./pages/blog/BlogPage";
import ArticlePage from "./pages/blog/ArticlePage";

// Admin Pages
import ArticleList from "./pages/admin/ArticleList";
import ArticleEditor from "./pages/admin/ArticleEditor";
import UserManagement from "./pages/admin/UserManagement";
import UserDetail from "./pages/admin/UserDetail";
import AutomationCommandCenter from "./pages/AutomationCommandCenter";

// Legal Pages
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import TermsOfService from "./pages/legal/TermsOfService";
import PrivacyNotice from "./pages/legal/PrivacyNotice";

// OAuth Callback Pages
import BlueButtonCallback from "./pages/BlueButtonCallback";

// Confidence Pulse (public response page)
import ConfidencePulseResponse from "./pages/ConfidencePulseResponse";

// Onboarding (BAA, Business Info, Payment, Twilio Setup)
import Onboarding from "./pages/Onboarding";

// Humana Integration Pages
import EnrollmentTracking from "./pages/EnrollmentTracking";

// Daily Recap
import DailyRecap from "./pages/DailyRecap";

// Daily Brief (comprehensive overnight risk scoring report)
import MorningBrief from "./pages/MorningBrief";

// Agency Platform Pages
import AgencyDashboard from "./pages/AgencyDashboard";
import AgencyRiskCenter from "./pages/AgencyRiskCenter";
import AgencyAgents from "./pages/AgencyAgents";
import AgencyClients from "./pages/AgencyClients";
import AgencySetup from "./pages/AgencySetup";
import AgencyRetentionCenter from "./pages/AgencyRetentionCenter";
import AgencyLeaderboard from "./pages/AgencyLeaderboard";
import AgencyCompliance from "./pages/AgencyCompliance";
import AgencyCommissions from "./pages/AgencyCommissions";
import AgencyCampaigns from "./pages/AgencyCampaigns";
import AgencyReports from "./pages/AgencyReports";
import AgencyEnrollmentTracker from "./pages/AgencyEnrollmentTracker";
import BookOfBusiness from "./pages/BookOfBusiness";
import RecruitingPipeline from "./pages/RecruitingPipeline";
import AgentScorecard from "./pages/AgentScorecard";
import ProspectingPipeline from "./pages/ProspectingPipeline";
import ImportProspects from "./pages/ImportProspects";

// FMO Platform Pages
import FmoDashboard from "./pages/fmo/FmoDashboard";
import FmoAgencies from "./pages/fmo/FmoAgencies";
import FmoAgencyDetail from "./pages/fmo/FmoAgencyDetail";
import FmoAgents from "./pages/fmo/FmoAgents";
import FmoAnalytics from "./pages/fmo/FmoAnalytics";
import FmoRisk from "./pages/fmo/FmoRisk";
import FmoRevenue from "./pages/fmo/FmoRevenue";
import FmoCompliance from "./pages/fmo/FmoCompliance";
import FmoEnrollment from "./pages/fmo/FmoEnrollment";
import FmoReports from "./pages/fmo/FmoReports";
import FmoSettings from "./pages/fmo/FmoSettings";

import ProtectedRoute from "./components/ProtectedRoute";

export default function App() {
  useEffect(() => {
    fetchCsrfToken(); // Non-blocking, non-fatal — wires CSRF token before user interaction
  }, []);

  return (
    <BrandingProvider>
    <ErrorBoundary>
    <Routes>
      {/* Marketing pages (public) */}
      {/* Using WaitlistLanding until April 2026 launch - no signups, just waitlist */}
      <Route path="/" element={<WaitlistLanding />} />
      <Route path="/pricing" element={<Pricing />} />
      {/* Old marketing routes redirect to home */}
      <Route path="/agents" element={<Navigate to="/#features" replace />} />
      <Route path="/agencies" element={<Navigate to="/" replace />} />
      {/* /fmo routes are now handled as protected app routes below */}

      {/* SEO Landing Pages (public, not in main nav) */}
      <Route path="/learn" element={<LearnIndex />} />
      <Route path="/learn/:slug" element={<LearnPage />} />
      <Route path="/solutions/:slug" element={<SolutionPage />} />

      {/* Blog/Articles (public) */}
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<ArticlePage />} />

      {/* Legal Pages (public) */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/privacy-notice" element={<PrivacyNotice />} />
      <Route path="/terms" element={<TermsOfService />} />

      {/* OAuth Callback Pages (public, minimal UI) */}
      <Route path="/bluebutton/callback" element={<BlueButtonCallback />} />

      {/* Confidence Pulse Response (public - for client survey responses) */}
      <Route path="/pulse/:pulseId" element={<ConfidencePulseResponse />} />

      {/* Auth (no layout) */}
      {/* Signup still available for pilot users (just not linked from landing page) */}
      <Route path="/signup" element={<Signup />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot" element={<ForgotPassword />} />
      <Route path="/reset" element={<ResetPassword />} />

      {/* Onboarding (BAA, Business Info, Payment, Twilio Setup) */}
      <Route path="/onboarding" element={<Onboarding />} />

      {/* Public helper routes for import/export pages */}
      <Route path="/clients/import" element={<ImportClients />} />
      <Route path="/clients/export" element={<ExportClients />} />

      {/* App routes (protected + layout) */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/founder-dashboard" element={<FounderDashboard />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/clients/new" element={<AddClient />} />
        <Route path="/clients/:id" element={<ClientProfile />} />
        <Route path="/tasks" element={<Tasks />} />
        <Route path="/policies" element={<Policies />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/phone-blast" element={<PhoneBlastPage />} />
        <Route path="/automations" element={<Automations />} />
        <Route path="/aep-wizard" element={<AEPWizard />} />
        <Route path="/aep-readiness" element={<AEPReadiness />} />
        <Route path="/oep" element={<OEPHub />} />
        <Route path="/risk" element={<RiskManagement />} />
        <Route path="/service" element={<ServiceDashboard />} />
        <Route path="/service/client/:clientId" element={<ServiceWorkspace />} />
        <Route path="/communications" element={<CommunicationsCenter />} />
        <Route path="/commissions" element={<Commissions />} />
        <Route path="/calendar" element={<Calendar />} />

        {/* Humana Integration */}
        <Route path="/enrollment-tracking" element={<EnrollmentTracking />} />

        {/* Daily Recap (legacy route) */}
        <Route path="/recap" element={<DailyRecap />} />

        {/* Daily Brief - comprehensive overnight scoring report */}
        <Route path="/daily-brief" element={<MorningBrief />} />
        <Route path="/morning-brief" element={<Navigate to="/daily-brief" replace />} />

        {/* Founder Command Center routes */}
        <Route path="/founder" element={<FounderCommandCenter />} />
        <Route path="/founder/adoption" element={<AgentAdoption />} />
        <Route path="/founder/client-quality" element={<ClientQuality />} />
        <Route path="/founder/value" element={<ValueDelivery />} />
        <Route path="/founder/system-health" element={<SystemHealth />} />
        <Route path="/founder/reports" element={<ReportsPage />} />

        {/* Audit Logs (admin only) */}
        <Route path="/audit-logs" element={<AuditLogsPage />} />

        {/* Admin Management */}
        <Route path="/admin/articles" element={<ArticleList />} />
        <Route path="/admin/articles/new" element={<ArticleEditor />} />
        <Route path="/admin/articles/:id" element={<ArticleEditor />} />
        <Route path="/admin/users" element={<UserManagement />} />
        <Route path="/admin/users/:id" element={<UserDetail />} />
        <Route path="/admin/automations" element={<AutomationCommandCenter />} />

        {/* Agency Platform */}
        <Route path="/agency/dashboard" element={<AgencyDashboard />} />
        <Route path="/agency/risk" element={<AgencyRiskCenter />} />
        <Route path="/agency/agents" element={<AgencyAgents />} />
        <Route path="/agency/agents/:agentId" element={<AgencyAgents />} />
        <Route path="/agency/agents/:agentId/scorecard" element={<AgentScorecard />} />
        <Route path="/agency/clients" element={<AgencyClients />} />
        <Route path="/agency/setup" element={<AgencySetup />} />
        <Route path="/agency/retention" element={<AgencyRetentionCenter />} />
        <Route path="/agency/leaderboard" element={<AgencyLeaderboard />} />
        <Route path="/agency/compliance" element={<AgencyCompliance />} />
        <Route path="/agency/commissions" element={<AgencyCommissions />} />
        <Route path="/agency/campaigns" element={<AgencyCampaigns />} />
        <Route path="/agency/reports" element={<AgencyReports />} />
        <Route path="/agency/enrollment-tracker" element={<AgencyEnrollmentTracker />} />
        <Route path="/agency/book-of-business" element={<BookOfBusiness />} />
        <Route path="/agency/recruiting" element={<RecruitingPipeline />} />
        <Route path="/prospecting" element={<ProspectingPipeline />} />
        <Route path="/prospecting/import" element={<ImportProspects />} />

        {/* FMO Platform */}
        <Route path="/fmo/dashboard" element={<FmoDashboard />} />
        <Route path="/fmo/agencies" element={<FmoAgencies />} />
        <Route path="/fmo/agencies/:agencyId" element={<FmoAgencyDetail />} />
        <Route path="/fmo/agents" element={<FmoAgents />} />
        <Route path="/fmo/analytics" element={<FmoAnalytics />} />
        <Route path="/fmo/risk" element={<FmoRisk />} />
        <Route path="/fmo/revenue" element={<FmoRevenue />} />
        <Route path="/fmo/compliance" element={<FmoCompliance />} />
        <Route path="/fmo/enrollment" element={<FmoEnrollment />} />
        <Route path="/fmo/reports" element={<FmoReports />} />
        <Route path="/fmo/settings" element={<FmoSettings />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
    </ErrorBoundary>
    </BrandingProvider>
  );
}
